import { ShoppingCart, Youtube, LogOut, User, Loader2, X, ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { usePageTransition } from '@/App';
import { useCart } from '@/context/CartContext';
import { useCheckout } from '@/context/CheckoutContext';
import { useDiscord } from '@/context/DiscordContext';
import { useProducts } from '@/context/ProductsContext';
import LiteModeToggle from '@/components/ui/LiteModeToggle';
import { trackSocialClick } from '@/utils/analytics';
import {
  ALL_PRODUCTS_FILTER,
  buildProductsPath,
  resolveProductsFilter
} from '@/utils/productFilters';

// Component-specific styles
import '@/styles/layout/Navigation.css';

// Discord logo SVG component (small version for navigation)
const DiscordIconSmall = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z"/>
  </svg>
);

const Navigation = ({ 
  mobileMenuOpen, 
  setMobileMenuOpen, 
  isCartOpen: propsIsCartOpen,
  setIsCartOpen: propsSetIsCartOpen, 
  scrollToHome,
  getTotalItems: propsGetTotalItems
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateWithTransition } = usePageTransition();
  const cartContext = useCart();
  const { loginWithFiveM, isLoading: authLoading, isAuthenticated, user, logout } = useCheckout();
  const { isConnected: isDiscordConnected, openModal: openDiscordModal } = useDiscord();
  const { filters, allProducts } = useProducts();
  
  // Logout loading state for smooth transition
  const [loggingOut, setLoggingOut] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  
  // Use context values, fallback to props for backward compatibility
  const isCartOpen = propsIsCartOpen !== undefined ? propsIsCartOpen : cartContext.isCartOpen;
  const setIsCartOpen = propsSetIsCartOpen || cartContext.setIsCartOpen;
  const getTotalItems = propsGetTotalItems || cartContext.getTotalItems;

  const YOUTUBE_URL = 'https://www.youtube.com/@retrostoredev';
  const DISCORD_URL = 'https://discord.gg/retrostore';
  const productCategories = useMemo(
    () => filters.filter((filter) => filter !== ALL_PRODUCTS_FILTER),
    [filters]
  );

  /* How many products sit behind each category. Shown in the menu because it
     is the thing a visitor actually wants to know before clicking — a count
     turns four words into four decisions. Derived from the catalogue rather
     than hard-coded, so it can never drift out of date. */
  const categoryCounts = useMemo(() => {
    const counts = new Map();
    for (const product of allProducts || []) {
      const key = product?.category;
      if (!key) continue;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }, [allProducts]);
  const activeProductFilter = useMemo(() => {
    if (location.pathname !== '/products') {
      return null;
    }

    const filterParam = new URLSearchParams(location.search).get('filter');
    return (
      resolveProductsFilter(filterParam, filters) ||
      (filterParam ? null : ALL_PRODUCTS_FILTER)
    );
  }, [filters, location.pathname, location.search]);
  const isProductsRoute = location.pathname === '/products';
  const isBundlesRoute = location.pathname === '/bundles';
  const isHomeRoute = location.pathname === '/';
  /* The floor, for the phone bar. A product page is still the catalogue as
     far as orientation goes; anything unlisted says nothing rather than
     guessing. */
  const currentFloor = (() => {
    const p = location.pathname;
    if (p === '/') return 'Home';
    if (p === '/products' || p.startsWith('/product/')) return 'Products';
    if (p === '/bundles') return 'Bundles';
    if (p === '/reviews') return 'Reviews';
    if (p === '/team') return 'Team';
    return null;
  })();
  const isReviewsRoute = location.pathname === '/reviews';
  const isTeamRoute = location.pathname === '/team';

  // Handle FiveM login - authentication only (no checkout)
  const handleFiveMLogin = async () => {
    try {
      await loginWithFiveM();
    } catch (err) {
      console.error('FiveM login error:', err);
    }
  };

  // Handle logout with smooth transition animation
  const handleLogout = () => {
    setLoggingOut(true);
    // Show loading for 1.2 seconds before actually logging out
    setTimeout(() => {
      logout();
      setLoggingOut(false);
    }, 1200);
  };

  // Handle navigation with filmstrip transition
  const handleNavClick = (e, path) => {
    e.preventDefault();
    
    // Close mobile menu if open
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    
    // Use filmstrip transition if available
    if (navigateWithTransition) {
      navigateWithTransition(path);
    } else {
      navigate(path);
    }
  };

  // Handle navigation to sections - if not on homepage, navigate there first then scroll
  const handleSectionClick = (e, sectionId) => {
    e.preventDefault();
    
    // Close mobile menu if open
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    
    if (location.pathname !== '/') {
      // Navigate to homepage with hash - let the App.js useEffect handle the scroll
      navigate('/' + sectionId);
    } else {
      // Already on homepage, just scroll
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleProductsMenuBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setProductsMenuOpen(false);
    }
  };

  return (
    <nav className="premium-navigation" data-testid="main-navigation">
      {/* The refraction the glass panel bends its backdrop with. It has to be
          a real SVG filter in the document for backdrop-filter to reference
          it; the element itself paints nothing.

          Turbulence generates a slow, organic field and feDisplacementMap
          pushes the backdrop's pixels along it, so straight edges passing
          behind the bar bow slightly rather than simply blurring. The scale
          is deliberately small — at high values it stops reading as glass and
          starts reading as a broken screen. */}
      <svg className="nav-glass-filter" aria-hidden="true" focusable="false">
        <filter id="nav-glass-refraction" x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.006 0.014" numOctaves="2" seed="4" result="noise" />
          <feGaussianBlur in="noise" stdDeviation="3" result="soft" />
          <feDisplacementMap in="SourceGraphic" in2="soft" scale="14" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </svg>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          <div className="flex-shrink-0">
            <a href="/" onClick={(e) => handleNavClick(e, '/')} className="nav-logo-link flex items-center" data-testid="logo-link">
              <div className="nav-logo-wrapper">
                <div className="nav-logo-glow"></div>
                <img 
                  src="/img/logo-alt-160.webp"
                  alt="Retro Store Logo" 
                  className="nav-logo-image"
                />
              </div>
            </a>
          </div>

          {/* Where you are, on the screen where the floor list is hidden.
              aria-hidden because the page's own heading already says it and a
              screen reader does not need to be told twice. */}
          {currentFloor && (
            <span className="nav-floor" aria-hidden="true">
              <span className="nav-floor-label">{currentFloor}</span>
            </span>
          )}
          
          <div className="hidden md:flex space-x-8 ml-12">
            <a
              href="#home"
              onClick={(e) => handleSectionClick(e, '#home')}
              className={`nav-primary-link text-gray-300 hover:text-accent transition-colors ${isHomeRoute ? 'is-current text-accent' : ''}`}
              data-testid="nav-home"
              aria-current={isHomeRoute ? 'page' : undefined}
            >
              <span className="nav-link-label">Home</span>
            </a>
            <div
              className={`nav-products-group ${productsMenuOpen ? 'is-open' : ''}`}
              onMouseEnter={() => setProductsMenuOpen(true)}
              onMouseLeave={() => setProductsMenuOpen(false)}
              onFocus={() => setProductsMenuOpen(true)}
              onBlur={handleProductsMenuBlur}
            >
              <a
                href="/products"
                onClick={(e) => handleNavClick(e, '/products')}
                className={`nav-primary-link nav-products-trigger text-gray-300 hover:text-accent transition-colors ${isProductsRoute ? 'is-current text-accent' : ''}`}
                data-testid="nav-products"
                aria-current={isProductsRoute ? 'page' : undefined}
                aria-haspopup={productCategories.length > 0 ? 'menu' : undefined}
                aria-expanded={productCategories.length > 0 ? productsMenuOpen : undefined}
              >
                <span className="nav-link-label">Products</span>
                {productCategories.length > 0 && (
                  <ChevronDown className="nav-products-chevron h-4 w-4" aria-hidden="true" />
                )}
              </a>
              {productCategories.length > 0 && (
                <div className="nav-products-dropdown" role="menu" aria-label="Product categories">
                  <span className="nav-products-notch" aria-hidden="true" />

                  <a
                    href="/products"
                    onClick={(e) => handleNavClick(e, '/products')}
                    className={`nav-products-item is-all ${activeProductFilter === ALL_PRODUCTS_FILTER ? 'active' : ''}`}
                    role="menuitem"
                  >
                    <span className="nav-products-item-label">All Products</span>
                    <span className="nav-products-item-count">{allProducts?.length || ''}</span>
                  </a>

                  <span className="nav-products-rule" aria-hidden="true" />

                  {productCategories.map((category) => (
                    <a
                      key={category}
                      href={buildProductsPath(category)}
                      onClick={(e) => handleNavClick(e, buildProductsPath(category))}
                      className={`nav-products-item ${activeProductFilter === category ? 'active' : ''}`}
                      role="menuitem"
                    >
                      <span className="nav-products-item-label">{category}</span>
                      <span className="nav-products-item-count">{categoryCounts.get(category) || ''}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
            <a
              href="/bundles"
              onClick={(e) => handleNavClick(e, '/bundles')}
              className={`nav-primary-link text-gray-300 hover:text-accent transition-colors ${isBundlesRoute ? 'is-current text-accent' : ''}`}
              data-testid="nav-bundles"
              aria-current={isBundlesRoute ? 'page' : undefined}
            >
              <span className="nav-link-label">Bundles</span>
            </a>
            <a
              href="/reviews"
              onClick={(e) => handleNavClick(e, '/reviews')}
              className={`nav-primary-link text-gray-300 hover:text-accent transition-colors ${isReviewsRoute ? 'is-current text-accent' : ''}`}
              data-testid="nav-reviews"
              aria-current={isReviewsRoute ? 'page' : undefined}
            >
              <span className="nav-link-label">Reviews</span>
            </a>
            <a
              href="/team"
              onClick={(e) => handleNavClick(e, '/team')}
              className={`nav-primary-link text-gray-300 hover:text-accent transition-colors ${isTeamRoute ? 'is-current text-accent' : ''}`}
              data-testid="nav-team"
              aria-current={isTeamRoute ? 'page' : undefined}
            >
              <span className="nav-link-label">Team</span>
            </a>
          </div>

          <div className="hidden md:flex items-center gap-4 ml-auto">
            {isAuthenticated && !loggingOut ? (
              /* Show user info when logged in */
              <div className="user-logged-in">
                {/* Discord connect button - shown if not connected */}
                {!isDiscordConnected && (
                  <button
                    type="button"
                    onClick={openDiscordModal}
                    className="nav-discord-connect-btn"
                    aria-label="Connect Discord account"
                    title="Connect Discord"
                  >
                    <DiscordIconSmall size={16} />
                  </button>
                )}
                <div className="user-info">
                  <User className="w-4 h-4" />
                  <span className="user-name">{user?.username || 'FiveM User'}</span>
                </div>
                <button type="button" 
                  onClick={handleLogout}
                  className="logout-btn"
                  aria-label="Logout"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : loggingOut ? (
              /* Show loading animation during logout */
              <div className="user-logged-in logout-loading">
                <Loader2 className="w-4 h-4 animate-spin text-accent" />
              </div>
            ) : (
              <Button 
                onClick={handleFiveMLogin}
                className="keymaster-login-btn"
                data-testid="login-button"
                aria-label="Login with FiveM account"
                disabled={authLoading}
              >
                <img 
                  src="/img/fivem-144.webp"
                  alt="FiveM logo" 
                  className="fivem-logo"
                />
                {authLoading ? 'Loading...' : 'Login with FiveM'}
              </Button>
            )}
            <button type="button" 
              onClick={() => setIsCartOpen(!isCartOpen)}
              className="relative text-gray-400 hover:text-accent transition-colors" 
              data-testid="cart-button"
              aria-label="Shopping cart"
            >
              <ShoppingCart className="h-5 w-5" />
              {getTotalItems() > 0 && (
                <span className="cart-badge">{getTotalItems()}</span>
              )}
            </button>
            <span className="text-gray-600">|</span>
            <a 
              href={YOUTUBE_URL}
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-400 hover:text-accent transition-colors" 
              data-testid="youtube-link"
              aria-label="Visit our YouTube channel"
              onClick={() => trackSocialClick('youtube', YOUTUBE_URL)}
            >
              <Youtube className="h-5 w-5" />
            </a>
            <a 
              href={DISCORD_URL}
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-400 hover:text-accent transition-colors" 
              data-testid="discord-link"
              aria-label="Join our Discord community"
              onClick={() => trackSocialClick('discord', DISCORD_URL)}
            >
              <svg className="h-5 w-5" viewBox="0 0 71 55" fill="currentColor">
                <g clipPath="url(#clip0)">
                  <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                </g>
              </svg>
            </a>
            <a 
              href="https://docs.retrostore.dev" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-accent hover:border-accent" data-testid="docs-button">
                Documentation
              </Button>
            </a>
            {/* Animation preference belongs with the other utilities; beside
                the wordmark it competed with the brand for the best pixels. */}
            <LiteModeToggle />
          </div>

          {/* Mobile: Cart button + Hamburger menu */}
          <div className="md:hidden flex items-center gap-3 ml-auto">
            <button
              onClick={() => setIsCartOpen(!isCartOpen)}
              className="relative text-gray-400 hover:text-accent transition-colors"
              data-testid="mobile-cart-button"
              aria-label="Shopping cart"
            >
              <ShoppingCart className="h-5 w-5" />
              {getTotalItems() > 0 && (
                <span className="cart-badge">{getTotalItems()}</span>
              )}
            </button>
            <button
              className="text-gray-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileMenuOpen}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu - Slide-in drawer from right (portaled to body to escape nav stacking context) */}
      {createPortal(
        <>
          <div className={`mobile-menu-backdrop ${mobileMenuOpen ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)} />
          <div className={`mobile-menu-drawer ${mobileMenuOpen ? 'active' : ''}`} data-testid="mobile-menu">
        {/* Drawer Header */}
        <div className="mobile-drawer-header">
          <img 
            src="/img/logo-alt-160.webp"
            alt="RetroStore" 
            className="mobile-drawer-logo"
          />
          <button 
            className="mobile-drawer-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Navigation Links */}
        <nav className="mobile-drawer-nav">
          <a
            href="#home"
            onClick={(e) => handleSectionClick(e, '#home')}
            className={`mobile-nav-link ${isHomeRoute && !location.hash ? 'active' : ''}`}
            aria-current={isHomeRoute && !location.hash ? 'page' : undefined}
          >
            Home
          </a>
          {/* No category level here. The products page carries those filters
              itself, all five visible at once since this pass, and one of them
              was called Bundles — the same word as the item below, pointing
              somewhere else entirely. */}
          <a
            href="/products"
            onClick={(e) => handleNavClick(e, '/products')}
            className={`mobile-nav-link ${isProductsRoute ? 'active' : ''}`}
            aria-current={isProductsRoute ? 'page' : undefined}
          >
            Products
          </a>
          <a
            href="/bundles"
            onClick={(e) => handleNavClick(e, '/bundles')}
            className={`mobile-nav-link ${isBundlesRoute ? 'active' : ''}`}
            aria-current={isBundlesRoute ? 'page' : undefined}
          >
            Bundles
          </a>
          <a
            href="/reviews"
            onClick={(e) => handleNavClick(e, '/reviews')}
            className={`mobile-nav-link ${location.pathname === '/reviews' ? 'active' : ''}`}
            aria-current={location.pathname === '/reviews' ? 'page' : undefined}
          >
            Reviews
          </a>
          <a
            href="/team"
            onClick={(e) => handleNavClick(e, '/team')}
            className={`mobile-nav-link ${isTeamRoute ? 'active' : ''}`}
            aria-current={isTeamRoute ? 'page' : undefined}
          >
            Team
          </a>
        </nav>

        {/* Divider */}
        <div className="mobile-drawer-divider" />

        {/* User Section */}
        <div className="mobile-drawer-user">
          {isAuthenticated && !loggingOut ? (
            <>
              <div className="mobile-user-info">
                <div className="mobile-user-avatar">
                  <User className="w-4 h-4" />
                </div>
                <span className="mobile-user-name">{user?.username || 'FiveM User'}</span>
                <button type="button"
                  onClick={handleLogout}
                  className="mobile-logout-btn"
                  aria-label="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
              {!isDiscordConnected && (
                <button
                  type="button"
                  onClick={openDiscordModal}
                  className="mobile-discord-btn"
                  aria-label="Connect Discord account"
                >
                  <DiscordIconSmall size={16} />
                  <span>Connect Discord</span>
                </button>
              )}
            </>
          ) : loggingOut ? (
            <div className="mobile-loading">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Logging out...</span>
            </div>
          ) : (
            <Button
              onClick={handleFiveMLogin}
              className="keymaster-login-btn mobile-login-btn"
              data-testid="mobile-login-button"
              disabled={authLoading}
            >
              <img src="/img/fivem-144.webp" alt="FiveM" className="fivem-logo" />
              {authLoading ? 'Loading...' : 'Login with FiveM'}
            </Button>
          )}
        </div>

        {/* Footer */}
        <div className="mobile-drawer-footer">
          {/* The desktop utility cluster is hidden below md, so the animation
              preference lives here — it matters most on phones. */}
          <div className="mobile-drawer-lite-toggle">
            <LiteModeToggle />
          </div>
          <div className="mobile-social-links">
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              onClick={() => trackSocialClick('youtube', YOUTUBE_URL)}
            >
              <Youtube className="w-5 h-5" />
            </a>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Discord"
              onClick={() => trackSocialClick('discord', DISCORD_URL)}
            >
              <svg className="w-5 h-5" viewBox="0 0 71 55" fill="currentColor">
                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
              </svg>
            </a>
          </div>
          <a href="https://docs.retrostore.dev" target="_blank" rel="noopener noreferrer" className="mobile-docs-link">
            Documentation →
          </a>
        </div>
      </div>
        </>,
        document.body
      )}
    </nav>
  );
};

export default Navigation;
